import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ChangeHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'production' | 'sample' | 'express_order' | 'express_sample';
  entityId: string | undefined | null;
  createdAt?: string | null;
  createdByEmail?: string | null;
}

const ChangeHistoryModal: React.FC<ChangeHistoryModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  createdAt,
  createdByEmail,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  const fetchHistory = useCallback(async () => {
    if (!isOpen) return;
    if (!entityId || entityId === 'undefined') return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('entity_change_log')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId as string)
        .order('created_at', { ascending: false });
      if (!error) setItems(data || []);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, entityType, entityId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Bloqueo del scroll del body
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  // Interceptar Escape a nivel documento en CAPTURE para que no lo tome el modal padre
  useEffect(() => {
    if (!isOpen) return;
    const onKeydownCapture = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Bloquear propagación a cualquier listener del padre
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          (e as any).stopImmediatePropagation();
        }
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeydownCapture, { capture: true });
    return () => document.removeEventListener('keydown', onKeydownCapture, { capture: true } as any);
  }, [isOpen, onClose]);

  // Enfocar contenedor para capturar Escape sin propagar al modal padre
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastEntityIdRef = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (isOpen) {
      containerRef.current?.focus();
    }
  }, [isOpen]);

  // Si cambia el entityId mientras el historial está abierto, lo cerramos para evitar que quede visible por error
  useEffect(() => {
    if (!isOpen) {
      lastEntityIdRef.current = entityId;
      return;
    }
    if (isOpen && lastEntityIdRef.current && entityId && lastEntityIdRef.current !== entityId) {
      requestAnimationFrame(() => onClose());
    }
    lastEntityIdRef.current = entityId;
  }, [entityId, isOpen, onClose]);

  // Cierre seguro: detiene propagación y difiere el cierre para evitar que el modal padre consuma el mismo evento
  const safeClose = (e?: { stopPropagation: () => void; preventDefault: () => void }) => {
    try {
      e?.stopPropagation();
      e?.preventDefault();
    } catch {}
    // Deferir al siguiente frame asegura que todos los handlers del click actual hayan terminado
    requestAnimationFrame(() => onClose());
  };

  // (Eliminado) No bloqueamos clics globales persistentes; gestionamos eventos localmente

  const getFieldLabel = (field: string): string => {
    const fieldLabels: Record<string, string> = {
      // Production fields
      'name': 'Nombre',
      'client_id': 'Cliente',
      'salesperson': 'Vendedor',
      'delivery_date': 'Fecha de entrega',
      'status': 'Estado',
      'priority': 'Prioridad',
      'notes': 'Notas',
      'approved_file': 'Archivo de ficha aprobada',
      'production_file': 'Archivo de producción',
      'finished_at': 'Fecha de terminación',
      
      // Sample fields
      'sample_type': 'Tipo de muestra',
      'sample_status': 'Estado de muestra',
      'sample_priority': 'Prioridad de muestra',
      'sample_notes': 'Notas de muestra',
      'sample_file': 'Archivo de muestra',
      
      // Express fields
      'express_value': 'Valor express',
      'express_notes': 'Notas express',
      'express_file': 'Archivo express',
      
      // Express Sample fields
      'sample_value': 'Valor de muestra',
      'sample_express_notes': 'Notas de muestra express',
      'sample_express_file': 'Archivo de muestra express',
      
      // Common fields
      'created_at': 'Fecha de creación',
      'updated_at': 'Fecha de actualización',
      'file_names': 'Archivos'
    };
    
    return fieldLabels[field] || field;
  };

  const formatChangeMessage = (field: string, oldValue: any, newValue: any): string => {
    // Caso especial para archivos aprobados
    if (field === 'approved_file') {
      if (oldValue && newValue) {
        return 'Se reemplazó el archivo de la ficha aprobada';
      } else if (newValue && !oldValue) {
        return 'Se agregó archivo de ficha aprobada';
      } else if (!newValue && oldValue) {
        return 'Se eliminó archivo de ficha aprobada';
      }
    }
    
    // Caso especial para otros archivos
    if (field.includes('_file') || field === 'file_names') {
      if (oldValue && newValue) {
        return 'Se reemplazó el archivo';
      } else if (newValue && !oldValue) {
        return 'Se agregó archivo';
      } else if (!newValue && oldValue) {
        return 'Se eliminó archivo';
      }
    }
    
    // Caso general
    return `${formatValue(oldValue)} → ${formatValue(newValue)}`;
  };

  const formatValue = (v: any): string => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'string' && v.length > 60) return v.slice(0, 57) + '…';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  if (!isOpen || !entityId || entityId === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center pointer-events-auto"
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      ref={containerRef}
      onKeyDownCapture={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          onClose();
        }
      }}
      onWheelCapture={(e) => {
        // Evita que el scroll afecte al contenido detrás
        if (!(e.target as HTMLElement).closest('[data-history-content]')) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onClickCapture={(e) => {
        if (!(e.target as HTMLElement).closest('[data-history-content]')) {
          e.stopPropagation();
        }
      }}
      onMouseDownCapture={(e) => { e.stopPropagation(); }}
    >
      <div
        className="absolute inset-0 bg-black/50 cursor-pointer z-[999999]"
        onMouseDown={(e) => safeClose(e)}
        onClick={(e) => safeClose(e)}
        onWheelCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
      />
      <div
        data-history-root
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl z-[1000000] max-h-[80vh] overflow-hidden pointer-events-auto"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onWheelCapture={(e) => {
          // Permite scroll sólo dentro del contenido
          if (!(e.target as HTMLElement).closest('[data-history-scroll]')) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
      >
        <div className="p-6 overflow-y-auto max-h-[70vh]" data-history-content data-history-scroll>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Historial de cambios</h3>
            <button
              type="button"
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              onClick={(e) => safeClose(e)}
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

        {createdAt && (
          <div className="border border-gray-200 dark:border-gray-700 rounded p-3 mb-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {new Date(createdAt).toLocaleString()} · {createdByEmail || ''}
            </div>
            <div className="text-sm text-gray-800 dark:text-gray-200">Creación</div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-gray-500">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500">Sin cambios registrados.</p>
        ) : (
          <div className="space-y-3">
            {items.map((h) => (
              <div key={h.id} className="border border-gray-200 dark:border-gray-700 rounded p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {new Date(h.created_at).toLocaleString()} · {h.changed_by_email || ''}
                </div>
                <ul className="list-disc pl-5 text-sm text-gray-800 dark:text-gray-200">
                  {(h.changes || []).map((c: any, idx: number) => (
                    <li key={idx}>
                      <span className="font-medium">{getFieldLabel(c.field)}</span>: {formatChangeMessage(c.field, c.oldValue, c.newValue)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        </div>
        <div className="sticky bottom-0 w-full bg-white/95 dark:bg-gray-800/95 backdrop-blur px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
            onClick={(e) => safeClose(e)}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangeHistoryModal;


